import Member from '../iam/models/Member'
import { CursorPaged, CursorPageQuery, Id } from 'types/definitions'
import {
  GetManyMembersByPublicId,
  PrimeMemberByPublicId,
} from 'iam/MemberStore'
import { WorkAPI } from './API'
import { isNotNil } from '../utils/object-util'
import Job from './Job'
import { formatMemberOption } from 'components/inputs/CustomSelect'
import OpportunityStore from 'opportunities/OpportunityStore'
import { GetMemberByPublicIdPayload } from 'iam/dtos/member'

import { getFeatureToggleClient } from '../infra/featureToggles'
import { ListPreviousProvidersRequest } from '@taxfyle/api-internal/internal/draft_pb'

/**
 * Provider Store.
 */
export default class ProviderStore {
  constructor(
    private workAPI: WorkAPI,
    private primeMemberByPublicId: PrimeMemberByPublicId,
    private getManyMembers: GetManyMembersByPublicId,
    private opportunityStore: OpportunityStore
  ) {}

  /**
   * Fetches available providers for the specified workspace member
   * (must be the current user).
   *
   * @param member
   */
  async fetchAvailableProvidersForMember(member: Member): Promise<Member[]> {
    if (!member.isCurrentUser) {
      throw new Error('Cannot fetch previous providers for non-current user.')
    }

    const useV3ListPreviousProviders = await getFeatureToggleClient().variation(
      'Portals.UseV3ListPreviousProviders',
      false
    )

    if (useV3ListPreviousProviders) {
      const request = new ListPreviousProvidersRequest()
        .setWorkspaceId(Id.assert(member.workspace.id))
        .setUserPublicId(Id.assert(member.user.publicId))

      const providers = await this.workAPI.drafts
        .listPreviousProviders(request)
        .then((x) => x.toObject())
        .then((x) => x.previousProvidersList)

      return providers
        .map((x) =>
          this.primeMemberByPublicId({
            user_public_id: x.userPublicId,
            family_name: x.familyName,
            given_name: x.givenName,
            picture: x.picture,
            workspace_id: x.workspaceId,
            display_name: x.displayName,
          })
        )
        .filter(isNotNil)
    }

    return this.fetchAvailableProvidersForMemberLegacy(member)
  }

  /**
   * Fetches qualified providers for a job.
   *
   * @param searchText
   * @param query
   * @param job
   */
  async fetchQualifiedProviders(
    searchText: string,
    query: CursorPageQuery,
    job: Job
  ): Promise<CursorPaged<Member | null>> {
    const lowerSearchText = searchText.toLowerCase()
    const pageToken = query.after ?? ''

    const response = await this.opportunityStore.listQualifiedProviders(
      Id.assert(job.id),
      25,
      pageToken
    )

    const qualifiedProsIds = response
      .getQualifiedProvidersList()
      .map((candidatesIds) => candidatesIds.getUserPublicId())

    const getMembersPayload: GetMemberByPublicIdPayload[] =
      qualifiedProsIds.map((id) => ({
        workspace_id: Id.assert(job.workspace.id),
        user_public_id: Id.assert(id),
      }))

    const result = await this.getManyMembers(getMembersPayload)

    const getNextPageToken = response.getNextPageToken()

    const paged = CursorPaged.fromArray(
      result,
      getNextPageToken === '' ? null : getNextPageToken
    )

    return CursorPaged.map(paged, (data) =>
      data.filter((m) => {
        if (!m) {
          return false
        }
        const memberInfo = formatMemberOption(m)

        return memberInfo.toLowerCase().includes(lowerSearchText)
      })
    )
  }

  /**
   * Fetches the preovious providers using Work API.
   */
  private async fetchAvailableProvidersForMemberLegacy(
    member: Member
  ): Promise<Member[]> {
    const providers =
      await this.workAPI.providers.fetchPreviousProvidersForCurrentUser({
        workspace_id: Id.assert(member.workspace.id),
      })

    return providers.map(this.primeMemberByPublicId).filter(isNotNil)
  }
}
